import React from "react";
import { Target, UserCheck, Users } from "lucide-react";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Progress } from "./ui/progress";
import { fmtINR } from "../lib/api";
import { TARGET_STATUS_META } from "./SupervisorTargetDialog";

function TargetCard({ icon: Icon, title, hint, target, collected, pct, expected, status, testId }) {
  const meta = TARGET_STATUS_META[status] || TARGET_STATUS_META.no_target;
  const bar = target ? Math.min(100, Math.round((collected || 0) * 100 / target)) : 0;
  return (
    <Card className="shadow-sm" data-testid={testId}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {title}</p>
            <p className="text-[11px] text-muted-foreground">{hint}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] shrink-0 ${meta.cls}`} data-testid={`${testId}-status`}>{meta.label}</Badge>
        </div>
        <p className="text-lg font-semibold tabular-nums" data-testid={`${testId}-value`}>
          {fmtINR(collected)} <span className="text-sm font-normal text-muted-foreground">/ {target != null ? fmtINR(target) : "no target"}</span>
        </p>
        {target ? (
          <div className="flex items-center gap-2">
            <Progress value={bar} className="h-2" />
            <span className="text-xs tabular-nums text-muted-foreground w-12 text-right">{pct != null ? `${pct}%` : "0%"}</span>
          </div>
        ) : null}
        {expected != null && <p className="text-[11px] text-muted-foreground">Expected by today: {fmtINR(expected)}</p>}
      </CardContent>
    </Card>
  );
}

/** Supervisor's own admin-assigned targets: Personal (vs own collections) + Team goal (vs team collections)
 *  alongside the bottom-up Team target (sum of collector targets). `mt` = my_targets row from the API. */
export function MyTargetsCards({ mt, compact = false }) {
  if (!mt) return null;
  return (
    <div className={`grid grid-cols-1 ${compact ? "md:grid-cols-3" : "md:grid-cols-3"} gap-3`} data-testid="my-targets-cards">
      <TargetCard icon={UserCheck} title="My personal target" hint="Set by Admin • your own collections"
        target={mt.personal_target} collected={mt.personal_collected} pct={mt.personal_achievement_pct}
        expected={mt.personal_expected_to_date} status={mt.personal_status} testId="my-personal-target" />
      <TargetCard icon={Users} title="Team goal" hint="Set by Admin • whole team's collections"
        target={mt.team_goal} collected={mt.team_collected} pct={mt.team_goal_achievement_pct}
        expected={mt.team_goal_expected_to_date} status={mt.team_goal_status} testId="my-team-goal" />
      <TargetCard icon={Target} title="Team target" hint="Sum of the collector targets you set"
        target={mt.team_target || null} collected={mt.team_collected} pct={mt.team_target_achievement_pct}
        status={mt.team_target ? (mt.team_target_achievement_pct >= 100 ? "achieved" : "on_track") : "no_target"} testId="my-team-target" />
    </div>
  );
}
